
// Manages render layers: state, logic, and sorted arrays for rendering.

/** @import { Model } from "./Model" */
/** @import THREE from "three" */

import { USE_MULTI_MATERIAL_RENDER_CALLS } from "../wgs/globals";

/**
 * Render target for a layer - determines where the layer is rendered.
 * @typedef {'main'|'overlay'} RenderTarget
 *
 * @private
 */

/**
 * Options for configuring a render layer.
 * @typedef {Object} RenderLayerOptions
 * @property {number} [renderOrder=0] - Global ordering within the same renderTarget.
 *   For 'main' target: negative values render before the main scene, positive values render after.
 *   For 'overlay' target: higher values render later.
 * @property {string} [renderTarget='overlay'] - Where to render this layer.
 *   'main' - Rendered during the main pass (before/after main scene based on renderOrder)
 *   'overlay' - Rendered during the overlay pass (after all main content)
 * @property {boolean} [clearDepthAfter=false] - Clear the depth buffer after rendering this layer.
 *   Useful when subsequent layers should render without depth interference from this layer. Only available for 'main' renderTarget.
 * @property {boolean} [visible=true] - Whether this layer is visible.
 */

/**
 * Manages render layers for adding custom content that renders alongside the main scene.
 *
 * Render layers allow extra content to render at specific points in the pipeline:
 *
 * - **Main target** (`renderTarget: 'main'`): Layers render during the main pass.
 *   Use `renderOrder < 0` to render before the main scene, `renderOrder > 0` to render after.
 *
 * - **Overlay target** (`renderTarget: 'overlay'`): Layers render during the overlay pass,
 *   after all main content has been rendered.
 *
 * @alias Autodesk.Viewing.RenderLayerManager
 */
export class RenderLayerManager {

  // TODO Make proper JSDOC again when we can hide the constructor in the docs pipeline
  // @param {Viewer3DImpl} impl - The viewer implementation instance
  constructor(impl) {
    this.impl = impl;

    // Map to manage layers by id
    this.renderLayers = new Map();
    // Sorted arrays to efficiently access (visible) layers by renderTarget and renderOrder
    this._mainBeforeLayers = []; // Visible Layers with renderTarget: 'main', rendered before main scene
    this._mainAfterLayers = []; // Visible Layers with renderTarget: 'main', rendered after main scene
    this._overlayLayers = []; // Visible Layers with renderTarget: 'overlay', rendered after all main content
  }

  /**
   * @private
   */
  dtor() {
    this.renderLayers.clear();
    this._mainBeforeLayers = [];
    this._mainAfterLayers = [];
    this._overlayLayers = [];
    this.impl = null;
  }

  /**
   * Rebuilds sorted arrays of visible layers by renderTarget and renderOrder.
   * @private
   */
  _updateRenderLayerArrays() {
    this._mainBeforeLayers = [];
    this._mainAfterLayers = [];
    this._overlayLayers = [];

    const sortByRenderOrder = (a, b) => a.renderOrder - b.renderOrder;

    for (const layer of this.renderLayers.values()) {
      if (layer.visible === false) continue;

      if (layer.renderTarget === 'main') {
        if (layer.renderOrder < 0) {
          this._mainBeforeLayers.push(layer);
        } else {
          this._mainAfterLayers.push(layer);
        }
      } else {
        this._overlayLayers.push(layer);
      }
    }

    this._mainBeforeLayers.sort(sortByRenderOrder);
    this._mainAfterLayers.sort(sortByRenderOrder);
    this._overlayLayers.sort(sortByRenderOrder);
  }

  /**
   * Creates a new render layer with the specified options.
   *
   * @param {string} id - Unique identifier for the layer
   * @param {RenderLayerOptions} [options] - Layer configuration options
   * @returns {boolean} True if the layer was created, false if it already exists or no id was provided
   *
   * @example
   *   // Create an overlay layer
   *   viewer.layers.create('annotations', {
   *       renderTarget: 'overlay',
   *       renderOrder: 10
   *   });
   *
   * @example
   *   // Create a layer that renders before the main scene
   *   viewer.layers.create('background', {
   *       renderTarget: 'main',
   *       renderOrder: -5
   *   });
   *
   * @alias Autodesk.Viewing.RenderLayerManager#create
   */
  create(id, options = {}) {
    if (!id) {
      return false;
    }

    if (this.renderLayers.has(id)) {
      return false;
    }

    const {
      renderOrder = 0,
      renderTarget = 'overlay',
      clearDepthAfter = false,
      visible = true
    } = options;

    // clearDepthAfter is only supported for main-pass layers. Overlay layers
    // share their depth buffer with the main color target — clearing it would
    // corrupt the main scene's depth and cause incorrect occlusion on
    // subsequent overlay-only re-renders (e.g. triggered by hover).
    if (clearDepthAfter && renderTarget !== 'main') {
      console.warn(`RenderLayer "${id}": clearDepthAfter is only supported with renderTarget 'main'. Ignored for '${renderTarget}'.`);
    }

    const layer = {
      id,
      scene: null,
      models: [],
      renderOrder,
      renderTarget,
      clearDepthAfter: renderTarget === 'main' ? clearDepthAfter : false,
      visible
    };

    this.renderLayers.set(id, layer);
    this._updateRenderLayerArrays();
    this.impl.invalidate(false, false, true);

    return true;
  }

  /**
   * Removes a render layer and all its contents.
   *
   * @param {string} id - The layer identifier
   * @returns {boolean} True if the layer was removed, false if it doesn't exist or no id was provided
   *
   * @alias Autodesk.Viewing.RenderLayerManager#remove
   */
  remove(id) {
    if (!id) {
      return false;
    }

    if (!this.renderLayers.has(id)) {
      console.warn(`RenderLayer "${id}" does not exist`);
      return false;
    }

    this.renderLayers.delete(id);
    this._updateRenderLayerArrays();
    this.impl.invalidate(false, false, true);

    return true;
  }

  /**
   * Checks whether a render layer exists.
   *
   * @param {string} id - The layer identifier
   * @returns {boolean} True if the layer exists
   *
   * @alias Autodesk.Viewing.RenderLayerManager#has
   */
  has(id) {
    if (!id) {
      return false;
    }

    return this.renderLayers.has(id);
  }

  /**
   * Adds or sets a THREE.Scene to a render layer.
   * Each layer can only have one scene. Adding a scene replaces any existing scene.
   *
   * @param {THREE.Scene} scene - The Three.js scene to add
   * @param {string} layerId - The layer identifier
   * @returns {boolean} True if the scene was added successfully
   * @private
   */
  addScene(scene, layerId) {
    if (!scene || !layerId) {
      return false;
    }

    const layer = this.renderLayers.get(layerId);
    if (!layer) {
      console.warn(`RenderLayer "${layerId}" does not exist`);
      return false;
    }

    layer.scene = scene;
    this.impl.invalidate(false, false, true);

    return true;
  }

  /**
   * Removes a THREE.Scene from a render layer.
   *
   * @param {THREE.Scene} scene - The Three.js scene to remove
   * @param {string} layerId - The layer identifier
   * @returns {boolean} True if the scene was removed successfully
   * @private
   */
  removeScene(scene, layerId) {
    if (!scene || !layerId) {
      return false;
    }

    const layer = this.renderLayers.get(layerId);
    if (!layer) {
      console.warn(`RenderLayer "${layerId}" does not exist`);
      return false;
    }

    if (layer.scene !== scene) {
      console.warn(`Scene not found in RenderLayer "${layerId}"`);
      return false;
    }

    layer.scene = null;
    this.impl.invalidate(false, false, true);

    return true;
  }

  /**
   * Gets the THREE.Scene from a render layer.
   *
   * @param {string} layerId - The layer identifier
   * @returns {THREE.Scene|null} The scene, or null if the layer has no scene
   * @private
   */
  getScene(layerId) {
    if (!layerId) {
      return null;
    }

    const layer = this.renderLayers.get(layerId);
    if (!layer) {
      console.warn(`RenderLayer "${layerId}" does not exist`);
      return null;
    }

    return layer.scene;
  }

  /**
   * Adds a Model to a render layer.
   * A layer can contain multiple models.
   *
   * @param {Model} model - The model to add
   * @param {string} layerId - The layer identifier
   * @returns {boolean} True if the model was added successfully
   *
   * @alias Autodesk.Viewing.RenderLayerManager#addModel
   */
  addModel(model, layerId) {
    if (!model || !layerId) {
      return false;
    }

    const layer = this.renderLayers.get(layerId);
    if (!layer) {
      console.warn(`RenderLayer "${layerId}" does not exist`);
      return false;
    }

    layer.models.push(model);

    if (!model.isInitialized()) {
      model.initialize(this.impl);
    }

    if (this.impl._useWebGPU || USE_MULTI_MATERIAL_RENDER_CALLS) {
      this.impl._webglrender.add3dModel(model);
    }

    this.impl.invalidate(false, false, true);

    return true;
  }

  /**
   * Removes a Model from a render layer.
   *
   * @param {Model} model - The model to remove
   * @param {string} layerId - The layer identifier
   * @returns {boolean} True if the model was removed successfully
   *
   * @alias Autodesk.Viewing.RenderLayerManager#removeModel
   */
  removeModel(model, layerId) {
    if (!model || !layerId) {
      return false;
    }

    const layer = this.renderLayers.get(layerId);
    if (!layer) {
      console.warn(`RenderLayer "${layerId}" does not exist`);
      return false;
    }

    const index = layer.models.indexOf(model);
    if (index === -1) {
      console.warn(`Model not found in RenderLayer "${layerId}"`);
      return false;
    }

    layer.models.splice(index, 1);
    this.impl.invalidate(false, false, true);

    return true;
  }

  /**
   * Gets all models from a render layer.
   *
   * @param {string} layerId - The layer identifier
   * @returns {Model[]} Array of models in the layer, or empty array if layer doesn't exist
   *
   * @alias Autodesk.Viewing.RenderLayerManager#getModels
   */
  getModels(layerId) {
    if (!layerId) {
      return [];
    }

    const layer = this.renderLayers.get(layerId);
    if (!layer) {
      console.warn(`RenderLayer "${layerId}" does not exist`);
      return [];
    }

    return [...layer.models];
  }

  /**
   * Clears all content (scene and models) from a render layer.
   * The layer itself is not removed.
   *
   * @param {string} layerId - The layer identifier
   * @returns {boolean} True if the layer was cleared successfully
   *
   * @alias Autodesk.Viewing.RenderLayerManager#clear
   */
  clear(layerId) {
    if (!layerId) {
      return false;
    }

    const layer = this.renderLayers.get(layerId);
    if (!layer) {
      console.warn(`RenderLayer "${layerId}" does not exist`);
      return false;
    }

    layer.scene = null;
    layer.models = [];
    this.impl.invalidate(false, false, true);

    return true;
  }

  /**
   * Updates the options for a render layer.
   *
   * @param {string} layerId - The layer identifier
   * @param {RenderLayerOptions} options - Options to update
   * @returns {boolean} True if the options were updated successfully
   *
   * @alias Autodesk.Viewing.RenderLayerManager#setLayerOptions
   */
  setLayerOptions(layerId, options) {
    if (!layerId) {
      return false;
    }

    const layer = this.renderLayers.get(layerId);
    if (!layer) {
      console.warn(`RenderLayer "${layerId}" does not exist`);
      return false;
    }

    let needsArrayUpdate = false;

    if (options.renderOrder !== undefined) {
      layer.renderOrder = options.renderOrder;
      needsArrayUpdate = true;
    }
    if (options.renderTarget !== undefined) {
      layer.renderTarget = options.renderTarget;
      needsArrayUpdate = true;
    }

    if (options.clearDepthAfter !== undefined && layer.renderTarget === 'main') {
      layer.clearDepthAfter = options.clearDepthAfter;
    } else {
      if (options.clearDepthAfter !== undefined) {
        console.warn(`RenderLayer "${layerId}": clearDepthAfter is only supported with renderTarget 'main'. Ignored for '${layer.renderTarget}'.`);
      }
      layer.clearDepthAfter = false;
    }

    if (options.visible !== undefined) {
      layer.visible = options.visible;
      needsArrayUpdate = true;
    }

    if (needsArrayUpdate) {
      this._updateRenderLayerArrays();
    }

    this.impl.invalidate(false, false, true);

    return true;
  }

  /**
   * Gets the current options for a render layer.
   *
   * @param {string} layerId - The layer identifier
   * @returns {RenderLayerOptions|null} The layer options, or null if layer doesn't exist
   *
   * @alias Autodesk.Viewing.RenderLayerManager#getLayerOptions
   */
  getLayerOptions(layerId) {
    if (!layerId) {
      return null;
    }

    const layer = this.renderLayers.get(layerId);
    if (!layer) {
      console.warn(`RenderLayer "${layerId}" does not exist`);
      return null;
    }

    return {
      renderOrder: layer.renderOrder,
      renderTarget: layer.renderTarget,
      clearDepthAfter: layer.clearDepthAfter,
      visible: layer.visible
    };
  }

  /**
   * Gets render layers for the overlay pass.
   * @returns {Array} Array of visible overlay render layers
   *
   * @alias Autodesk.Viewing.RenderLayerManager#getOverlayRenderLayers
   */
  getOverlayRenderLayers() {
    return this._overlayLayers;
  }

  /**
   * Gets render layers for the main pass, split by renderOrder.
   * @returns {{before: Array, after: Array}} Layers before and after main scene
   *
   * @alias Autodesk.Viewing.RenderLayerManager#getMainPassRenderLayers
   */
  getMainPassRenderLayers() {
    return {
      before: this._mainBeforeLayers,
      after: this._mainAfterLayers
    };
  }
}